import { useMemo } from 'react';
import { can, type BoardRole, type Permission } from '@taskflow/shared';
import { useAuth } from './AuthContext';

/**
 * Permission checks for the UI.
 *
 * Calls the exact same `can()` the Worker's `rbac` middleware calls — one matrix,
 * two consumers, no drift.
 *
 * **This hides controls; it does not secure anything.** Every check here is also
 * performed server-side, and that check is the security boundary. Hiding a button a
 * user cannot use is a courtesy that keeps the interface honest; treating it as
 * protection would mean anyone with the browser console has full access.
 */
export function usePermissions(boardRole?: BoardRole) {
  const { user } = useAuth();

  return useMemo(() => {
    const systemRole = user?.systemRole;

    const check = (permission: Permission): boolean => {
      if (!systemRole) return false;
      return can({ systemRole, boardRole }, permission);
    };

    return {
      can: check,
      canAny: (...permissions: readonly Permission[]) =>
        permissions.some((permission) => check(permission)),
      canAll: (...permissions: readonly Permission[]) =>
        permissions.every((permission) => check(permission)),
      isAdmin: systemRole === 'admin' || systemRole === 'super_admin',
      isSuperAdmin: systemRole === 'super_admin',
    };
  }, [user?.systemRole, boardRole]);
}
